/**
 * Obsidian — Fraud & Payment Protection Dashboard
 * Full alerts queue, risk metrics, config panel, audit log, restricted users.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '../AdminApp';
import { toast } from 'sonner';
import {
  AlertTriangle, RefreshCw, Play, Shield, Ban, Lock, Unlock,
  Eye, Search, DollarSign, Activity, BarChart3, FileText, Settings,
  XCircle, CheckCircle, Flag, TrendingUp, Users, CreditCard
} from 'lucide-react';

const CATEGORY_COLORS = {
  velocity_abuse: '#f59e0b', gift_ring: '#dc2626', chargeback_pattern: '#b91c1c',
  amount_anomaly: '#7c3aed', multi_account: '#ea580c', rapid_cashout: '#0891b2',
  failed_payment_spike: '#6366f1', suspicious_gifting: '#ec4899',
  new_account_high_spend: '#059669', refund_abuse: '#d97706', app_store_refund: '#0ea5e9',
};
const ACTION_STYLES = {
  flag: { bg: 'bg-amber-100', text: 'text-amber-800', icon: Flag },
  restrict: { bg: 'bg-orange-100', text: 'text-orange-800', icon: Lock },
  freeze: { bg: 'bg-red-100', text: 'text-red-800', icon: Ban },
  clear: { bg: 'bg-emerald-100', text: 'text-emerald-800', icon: Unlock },
  dismiss: { bg: 'bg-gray-100', text: 'text-gray-600', icon: XCircle },
};
const STATUS_STYLES = {
  pending: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Pending' },
  auto_actioned: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Auto' },
  resolved: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Resolved' },
  cleared: { bg: 'bg-teal-100', text: 'text-teal-800', label: 'Cleared' },
  dismissed: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Dismissed' },
};

const ObsidianDashboard = () => {
  const { api } = useAdmin();
  const [tab, setTab] = useState('queue');
  const [alerts, setAlerts] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [config, setConfig] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [restrictedUsers, setRestrictedUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [pendingCount, setPendingCount] = useState(0);

  const fetchAll = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterCat) params.append('category', filterCat);
      const [aRes, mRes, cRes] = await Promise.all([
        api.get(`/api/admin/workforce/obsidian/alerts?${params}`),
        api.get('/api/admin/workforce/obsidian/scan-metrics'),
        api.get('/api/admin/workforce/obsidian/config'),
      ]);
      setAlerts(aRes.data.alerts || []);
      setPendingCount(aRes.data.pending_count || 0);
      setMetrics(mRes.data);
      setConfig(cRes.data);
    } catch { toast.error('Failed to load Obsidian data'); }
    finally { setLoading(false); }
  }, [api, filterStatus, filterCat]);

  const fetchAudit = useCallback(async () => {
    try {
      const res = await api.get('/api/admin/workforce/obsidian/audit-log');
      setAuditLogs(res.data.logs || []);
    } catch {}
  }, [api]);

  const fetchRestricted = useCallback(async () => {
    try {
      const res = await api.get('/api/admin/workforce/obsidian/restricted-users');
      setRestrictedUsers(res.data.users || []);
    } catch {}
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { if (tab === 'audit') fetchAudit(); }, [tab, fetchAudit]);
  useEffect(() => { if (tab === 'restricted') fetchRestricted(); }, [tab, fetchRestricted]);

  const runScan = async () => {
    setActionLoading('scan');
    try {
      const res = await api.post('/api/admin/workforce/obsidian/scan');
      toast.success(`Scan complete: ${res.data.total_alerts} alerts generated`);
      fetchAll();
    } catch { toast.error('Scan failed'); }
    finally { setActionLoading(''); }
  };

  const takeAction = async (alertId, action, notes = '') => {
    setActionLoading(action);
    try {
      await api.post('/api/admin/workforce/obsidian/action', { alert_id: alertId, action, admin_notes: notes });
      toast.success(`${action.charAt(0).toUpperCase() + action.slice(1)} applied`);
      setSelected(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setActionLoading(''); }
  };

  const liftRestrictions = async (userId) => {
    try {
      await api.post(`/api/admin/workforce/obsidian/users/${userId}/lift-restrictions`);
      toast.success('Restrictions lifted');
      fetchRestricted();
    } catch { toast.error('Failed to lift restrictions'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="w-6 h-6 animate-spin text-purple-500" /></div>;
  const sensitivity = config?.config?.sensitivity_level || 'medium';
  const isEnabled = config?.config?.enabled !== false;

  return (
    <div className="p-6 space-y-5 overflow-y-auto h-full" data-testid="obsidian-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-purple-900 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900" data-testid="obsidian-title">Obsidian — Fraud & Payment Protection</h1>
            <p className="text-sm text-gray-500">Real-time transaction monitoring & fraud defense</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={runScan} disabled={!!actionLoading}
            className="flex items-center gap-1.5 px-4 py-2 bg-purple-900 text-white rounded-lg text-sm font-medium hover:bg-purple-950 disabled:opacity-50"
            data-testid="obsidian-scan-btn">
            <Play className={`w-4 h-4 ${actionLoading === 'scan' ? 'animate-pulse' : ''}`} />
            {actionLoading === 'scan' ? 'Scanning...' : 'Run Fraud Scan'}
          </button>
          <button onClick={fetchAll} className="flex items-center gap-1.5 px-3 py-2 border rounded-lg text-sm hover:bg-gray-50" data-testid="obsidian-refresh-btn">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {/* Config Status Bar */}
      <div className="bg-white border rounded-xl p-4">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
          <div>
            <span className="text-gray-500 text-xs">Status</span>
            <p className={`font-bold ${isEnabled ? 'text-emerald-600' : 'text-red-600'}`} data-testid="obsidian-status">
              {isEnabled ? 'ACTIVE' : 'DISABLED'}
            </p>
          </div>
          <div>
            <span className="text-gray-500 text-xs">Sensitivity</span>
            <p className={`font-bold ${sensitivity === 'high' ? 'text-red-600' : sensitivity === 'low' ? 'text-green-600' : 'text-amber-600'}`} data-testid="obsidian-sensitivity">
              {sensitivity.toUpperCase()}
            </p>
          </div>
          <div>
            <span className="text-gray-500 text-xs">Auto-Flag</span>
            <p className={`font-bold ${config?.config?.auto_flag_enabled ? 'text-emerald-600' : 'text-gray-400'}`}>
              {config?.config?.auto_flag_enabled ? 'ON' : 'OFF'}
            </p>
          </div>
          <div>
            <span className="text-gray-500 text-xs">Auto-Restrict</span>
            <p className={`font-bold ${config?.config?.auto_restrict_enabled ? 'text-emerald-600' : 'text-gray-400'}`}>
              {config?.config?.auto_restrict_enabled ? 'ON' : 'OFF'}
            </p>
          </div>
          <div>
            <span className="text-gray-500 text-xs">Auto-Freeze</span>
            <p className={`font-bold ${config?.config?.auto_freeze_enabled ? 'text-red-600' : 'text-gray-400'}`}>
              {config?.config?.auto_freeze_enabled ? 'ON' : 'OFF'}
            </p>
          </div>
          <div>
            <span className="text-gray-500 text-xs">Human Review</span>
            <p className={`font-bold ${config?.config?.human_review_required ? 'text-blue-600' : 'text-gray-400'}`}>
              {config?.config?.human_review_required ? 'REQUIRED' : 'OFF'}
            </p>
          </div>
        </div>
      </div>

      {/* Metrics Row */}
      {metrics && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
          {[
            { l: 'Monitored', v: metrics.total_monitored || 0, c: 'text-purple-700' },
            { l: 'Total Alerts', v: metrics.total_alerts, c: 'text-gray-700' },
            { l: 'Pending', v: metrics.pending, c: metrics.pending > 0 ? 'text-amber-600' : 'text-gray-600' },
            { l: 'Resolved', v: metrics.resolved, c: 'text-emerald-600' },
            { l: '24h Alerts', v: metrics.alerts_24h, c: 'text-blue-600' },
            { l: 'CB Rate', v: `${metrics.chargeback_rate}%`, c: metrics.chargeback_rate > 1 ? 'text-red-600' : 'text-gray-600' },
            { l: 'Frozen', v: metrics.frozen_users || 0, c: 'text-red-600' },
            { l: 'Restricted', v: metrics.restricted_users || 0, c: 'text-orange-600' },
          ].map(m => (
            <div key={m.l} className="bg-white border rounded-lg p-3 text-center" data-testid={`metric-${m.l.toLowerCase().replace(/\s/g, '-')}`}>
              <p className={`text-lg font-bold ${m.c}`}>{m.v}</p>
              <p className="text-[10px] text-gray-500">{m.l}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {[
          { k: 'queue', l: `Alerts Queue${pendingCount > 0 ? ` (${pendingCount})` : ''}`, icon: AlertTriangle },
          { k: 'breakdown', l: 'Breakdown', icon: BarChart3 },
          { k: 'restricted', l: 'Restricted Users', icon: Users },
          { k: 'audit', l: 'Audit Log', icon: FileText },
          { k: 'config', l: 'Settings', icon: Settings },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition ${tab === t.k ? 'border-purple-800 text-purple-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            data-testid={`obsidian-tab-${t.k}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.l}
          </button>
        ))}
      </div>

      {/* QUEUE TAB */}
      {tab === 'queue' && (
        <div className="space-y-3">
          <div className="flex gap-2 flex-wrap">
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              className="text-xs border rounded-lg px-2.5 py-1.5 bg-white" data-testid="obsidian-filter-status">
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="auto_actioned">Auto-Actioned</option>
              <option value="resolved">Resolved</option>
              <option value="cleared">Cleared</option>
              <option value="dismissed">Dismissed</option>
            </select>
            <select value={filterCat} onChange={e => setFilterCat(e.target.value)}
              className="text-xs border rounded-lg px-2.5 py-1.5 bg-white" data-testid="obsidian-filter-category">
              <option value="">All Categories</option>
              {Object.entries(config?.fraud_categories || {}).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Alerts List */}
            <div className="bg-white border rounded-xl overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4 text-purple-700" /> Fraud Alerts ({alerts.length})
              </div>
              <div className="divide-y max-h-[500px] overflow-y-auto">
                {alerts.length === 0 ? (
                  <div className="p-8 text-center text-gray-400 text-sm" data-testid="obsidian-no-alerts">No fraud alerts found. Run a scan to analyze transactions.</div>
                ) : alerts.map(a => {
                  const s = STATUS_STYLES[a.status] || STATUS_STYLES.pending;
                  return (
                    <div key={a.id}
                      onClick={() => setSelected(a)}
                      className={`px-4 py-3 cursor-pointer hover:bg-gray-50 transition ${selected?.id === a.id ? 'bg-purple-50 border-l-4 border-purple-800' : ''}`}
                      data-testid={`alert-${a.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <img src={a.user_picture || `https://ui-avatars.com/api/?name=${a.user_handle}&size=24&background=581c87&color=fff`} alt="" className="w-5 h-5 rounded-full" />
                          <span className="text-xs font-medium text-gray-800">@{a.user_handle}</span>
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${s.bg} ${s.text}`}>{s.label}</span>
                      </div>
                      <p className="text-sm text-gray-700">{a.category_label || a.category}</p>
                      <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-500">
                        <span className="px-1.5 py-0.5 rounded text-white font-medium" style={{ background: CATEGORY_COLORS[a.category] || '#6b7280' }}>
                          {(a.fraud_score * 100).toFixed(0)}% risk
                        </span>
                        <span>{a.signals?.length || 0} signals</span>
                        <span className="ml-auto">{a.created_at ? new Date(a.created_at).toLocaleTimeString() : ''}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Detail + Actions */}
            <div className="bg-white border rounded-xl overflow-hidden">
              {!selected ? (
                <div className="p-12 text-center text-gray-400">
                  <Eye className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p className="text-sm">Select an alert to investigate</p>
                </div>
              ) : (
                <div className="flex flex-col h-full max-h-[560px]">
                  <div className="px-4 py-3 bg-gray-50 border-b">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <img src={selected.user_picture || `https://ui-avatars.com/api/?name=${selected.user_handle}&size=32&background=581c87&color=fff`} alt="" className="w-8 h-8 rounded-full" />
                        <div>
                          <p className="font-semibold text-gray-900" data-testid="selected-user">@{selected.user_handle}</p>
                          <p className="text-[10px] text-gray-500">{selected.user_name} {selected.user_email ? `(${selected.user_email})` : ''}</p>
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${(STATUS_STYLES[selected.status] || STATUS_STYLES.pending).bg} ${(STATUS_STYLES[selected.status] || STATUS_STYLES.pending).text}`}>
                        {(STATUS_STYLES[selected.status] || STATUS_STYLES.pending).label}
                      </span>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {/* Risk Score */}
                    <div className="flex items-center gap-3 bg-purple-50 rounded-lg p-3">
                      <div className="text-center">
                        <p className={`text-2xl font-bold ${selected.fraud_score >= 0.7 ? 'text-red-600' : selected.fraud_score >= 0.4 ? 'text-amber-600' : 'text-gray-600'}`}>
                          {(selected.fraud_score * 100).toFixed(0)}%
                        </p>
                        <p className="text-[10px] text-gray-500">Fraud Score</p>
                      </div>
                      <div className="flex-1">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className={`h-2 rounded-full ${selected.fraud_score >= 0.7 ? 'bg-red-500' : selected.fraud_score >= 0.4 ? 'bg-amber-500' : 'bg-gray-400'}`}
                            style={{ width: `${selected.fraud_score * 100}%` }} />
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-bold text-red-600 uppercase">{selected.recommended_action}</p>
                        <p className="text-[10px] text-gray-500">Recommended</p>
                      </div>
                    </div>

                    {/* Signals */}
                    <div>
                      <span className="text-[10px] font-bold text-gray-400 uppercase">Detection Signals</span>
                      <div className="mt-1 space-y-1">
                        {(selected.signals || []).map((sig, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs bg-red-50 border border-red-100 rounded px-3 py-1.5">
                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: CATEGORY_COLORS[sig.category] || '#6b7280' }} />
                            <span className="font-medium">{sig.category?.replace(/_/g, ' ')}</span>
                            <span className="text-gray-500 flex-1">{sig.detail}</span>
                            <span className="text-gray-400">wt: {sig.weight}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Details grid */}
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase">Category</span>
                        <p className="font-medium" style={{ color: CATEGORY_COLORS[selected.category] || '#6b7280' }}>
                          {selected.category_label || selected.category}
                        </p>
                      </div>
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase">Detected</span>
                        <p className="font-medium">{selected.created_at ? new Date(selected.created_at).toLocaleString() : 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase">Transaction IDs</span>
                        <p className="font-mono text-xs truncate">{(selected.transaction_ids || []).slice(0, 3).join(', ') || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase">Prior Alerts</span>
                        <p className="font-medium">{selected.user_fraud_history?.total_alerts ?? selected.details?.prior_alerts ?? 'N/A'}</p>
                      </div>
                    </div>

                    {/* Details box */}
                    {selected.details && Object.keys(selected.details).length > 0 && (
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase">Additional Details</span>
                        <div className="mt-1 bg-gray-50 rounded-lg p-3 text-xs font-mono space-y-1">
                          {Object.entries(selected.details).map(([k, v]) => (
                            <div key={k} className="flex justify-between">
                              <span className="text-gray-500">{k.replace(/_/g, ' ')}</span>
                              <span className="font-medium text-gray-800">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {selected.action_taken && (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                        <span className="text-[10px] font-bold text-emerald-600 uppercase">Action Taken</span>
                        <p className="text-sm font-medium text-emerald-800">{selected.action_taken} — by {selected.resolved_by || 'system'}</p>
                        {selected.admin_notes && <p className="text-xs text-emerald-700 mt-1">{selected.admin_notes}</p>}
                      </div>
                    )}
                  </div>

                  {selected.status === 'pending' && (
                    <div className="border-t p-4 bg-gray-50">
                      <p className="text-xs font-semibold text-gray-600 mb-2">Take Action</p>
                      <div className="flex flex-wrap gap-2">
                        {['flag', 'restrict', 'freeze', 'clear', 'dismiss'].map(action => {
                          const s = ACTION_STYLES[action];
                          const Icon = s.icon;
                          return (
                            <button key={action}
                              onClick={() => takeAction(selected.id, action)}
                              disabled={!!actionLoading}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition ${s.bg} ${s.text} hover:opacity-80 disabled:opacity-50`}
                              data-testid={`obsidian-action-${action}`}>
                              <Icon className="w-3.5 h-3.5" /> {action.charAt(0).toUpperCase() + action.slice(1)}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* BREAKDOWN TAB */}
      {tab === 'breakdown' && metrics && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white border rounded-xl p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">By Category</h3>
            <div className="space-y-2">
              {Object.entries(metrics.category_breakdown || {}).sort((a, b) => b[1] - a[1]).map(([cat, count]) => (
                <div key={cat} className="flex items-center justify-between text-sm" data-testid={`breakdown-${cat}`}>
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: CATEGORY_COLORS[cat] || '#6b7280' }} />
                    <span className="text-gray-700 capitalize">{cat.replace(/_/g, ' ')}</span>
                  </div>
                  <span className="font-bold">{count}</span>
                </div>
              ))}
              {Object.keys(metrics.category_breakdown || {}).length === 0 && <p className="text-gray-400 text-sm">No data yet</p>}
            </div>
          </div>
          <div className="bg-white border rounded-xl p-4 space-y-4">
            <h3 className="text-sm font-semibold text-gray-700">Key Risk Indicators</h3>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Chargeback Rate (7d)</span>
                <span className={`font-bold ${metrics.chargeback_rate > 1 ? 'text-red-600' : 'text-emerald-600'}`}>{metrics.chargeback_rate}%</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Avg Fraud Score</span>
                <span className="font-bold text-gray-800">{(metrics.avg_fraud_score * 100).toFixed(0)}%</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Active Frozen Users</span>
                <span className={`font-bold ${metrics.frozen_users > 0 ? 'text-red-600' : 'text-gray-600'}`}>{metrics.frozen_users}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Active Restricted Users</span>
                <span className={`font-bold ${metrics.restricted_users > 0 ? 'text-orange-600' : 'text-gray-600'}`}>{metrics.restricted_users}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Flagged Users</span>
                <span className="font-bold text-amber-600">{metrics.flagged_users || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total Scans Run</span>
                <span className="font-bold text-gray-800">{metrics.total_scans}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* RESTRICTED USERS TAB */}
      {tab === 'restricted' && (
        <div className="bg-white border rounded-xl overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b text-sm font-semibold text-gray-700 flex items-center gap-1.5">
            <Users className="w-4 h-4" /> Users with Active Restrictions ({restrictedUsers.length})
          </div>
          <div className="divide-y max-h-[500px] overflow-y-auto">
            {restrictedUsers.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-sm" data-testid="no-restricted-users">No users currently restricted</div>
            ) : restrictedUsers.map(u => (
              <div key={u.id} className="px-4 py-3 flex items-center justify-between" data-testid={`restricted-user-${u.id}`}>
                <div className="flex items-center gap-3">
                  <img src={u.picture || `https://ui-avatars.com/api/?name=${u.handle || u.name}&size=32&background=581c87&color=fff`} alt="" className="w-8 h-8 rounded-full" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">@{u.handle || 'unknown'}</p>
                    <p className="text-xs text-gray-500">{u.name} — {u.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {u.payment_frozen && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">FROZEN</span>}
                  {u.payment_restricted && !u.payment_frozen && <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">RESTRICTED</span>}
                  {u.fraud_flagged && !u.payment_frozen && !u.payment_restricted && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">FLAGGED</span>}
                  <button onClick={() => liftRestrictions(u.id)}
                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 rounded hover:bg-emerald-100"
                    data-testid={`lift-${u.id}`}>
                    <Unlock className="w-3 h-3" /> Lift
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AUDIT LOG TAB */}
      {tab === 'audit' && (
        <div className="bg-white border rounded-xl overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b text-sm font-semibold text-gray-700">Fraud Enforcement Audit Trail</div>
          <div className="divide-y max-h-[500px] overflow-y-auto">
            {auditLogs.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-sm" data-testid="no-audit-logs">No enforcement actions yet</div>
            ) : auditLogs.map(l => (
              <div key={l.id} className="px-4 py-3 text-sm" data-testid={`audit-${l.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${(ACTION_STYLES[l.action] || ACTION_STYLES.flag).bg} ${(ACTION_STYLES[l.action] || ACTION_STYLES.flag).text}`}>
                      {l.action?.toUpperCase()}
                    </span>
                    <span className="font-medium text-gray-900">@{l.user_handle}</span>
                  </div>
                  <span className="text-xs text-gray-400">{l.created_at ? new Date(l.created_at).toLocaleString() : ''}</span>
                </div>
                <p className="text-xs text-gray-600 mt-1">{l.reason}</p>
                <p className="text-[10px] text-gray-400 mt-0.5">by {l.executed_by} {l.automated ? '(automated)' : ''}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CONFIG TAB */}
      {tab === 'config' && <ConfigPanel api={api} config={config} onSave={() => fetchAll()} />}
    </div>
  );
};

const ConfigPanel = ({ api, config, onSave }) => {
  const [form, setForm] = useState({
    sensitivity_level: config?.config?.sensitivity_level || 'medium',
    auto_flag_enabled: config?.config?.auto_flag_enabled ?? true,
    auto_restrict_enabled: config?.config?.auto_restrict_enabled ?? false,
    auto_freeze_enabled: config?.config?.auto_freeze_enabled ?? false,
    human_review_required: config?.config?.human_review_required ?? true,
    enabled: config?.config?.enabled ?? true,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post('/api/admin/workforce/obsidian/config', form);
      toast.success('Config saved');
      onSave();
    } catch { toast.error('Failed to save config'); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white border rounded-xl p-5 max-w-xl space-y-4" data-testid="obsidian-config-panel">
      <h3 className="text-sm font-semibold text-gray-700">Fraud Protection Settings</h3>

      <div>
        <label className="text-xs text-gray-500">Sensitivity Level</label>
        <select value={form.sensitivity_level} onChange={e => setForm(f => ({ ...f, sensitivity_level: e.target.value }))}
          className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="config-sensitivity">
          <option value="low">Low — fewer false positives</option>
          <option value="medium">Medium — balanced</option>
          <option value="high">High — aggressive detection</option>
        </select>
      </div>

      <div className="space-y-2">
        {[
          { k: 'enabled', l: 'Engine Enabled', d: 'Enable/disable the entire Obsidian engine' },
          { k: 'auto_flag_enabled', l: 'Auto-Flag', d: 'Automatically flag suspicious transactions' },
          { k: 'auto_restrict_enabled', l: 'Auto-Restrict', d: 'Automatically restrict payment access' },
          { k: 'auto_freeze_enabled', l: 'Auto-Freeze', d: 'Automatically freeze accounts (use with caution)' },
          { k: 'human_review_required', l: 'Human Review Required', d: 'Require admin review before enforcement' },
        ].map(opt => (
          <label key={opt.k} className="flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
            data-testid={`config-${opt.k}`}>
            <div>
              <p className="text-sm font-medium text-gray-800">{opt.l}</p>
              <p className="text-xs text-gray-500">{opt.d}</p>
            </div>
            <input type="checkbox" checked={form[opt.k]}
              onChange={e => setForm(f => ({ ...f, [opt.k]: e.target.checked }))}
              className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500" />
          </label>
        ))}
      </div>

      <button onClick={save} disabled={saving}
        className="px-4 py-2 bg-purple-900 text-white rounded-lg text-sm font-medium hover:bg-purple-950 disabled:opacity-50"
        data-testid="config-save-btn">
        {saving ? 'Saving...' : 'Save Configuration'}
      </button>

      {/* Thresholds Reference */}
      {config?.risk_thresholds && (
        <div className="mt-4 border-t pt-4">
          <h4 className="text-xs font-bold text-gray-400 uppercase mb-2">Current Thresholds ({form.sensitivity_level})</h4>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {Object.entries(config.risk_thresholds[form.sensitivity_level] || {}).map(([k, v]) => (
              <div key={k} className="flex justify-between bg-gray-50 rounded px-2 py-1">
                <span className="text-gray-500">{k.replace(/_/g, ' ')}</span>
                <span className="font-medium text-gray-800">{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ObsidianDashboard;
